import React from 'react';
import { Table, TableProps, TableColumnType, Row, Col } from 'antd';
import { Colors } from 'works/legacy/lib/style-helper';
import { TableFilterControls } from './table-filter-controls';
import { clsx } from 'clsx';

const stableScroll = { x: 1280 };

/*
 * Public Row Class Names:
 * 'highlight-recently-updated'
 * 'highlight-action-required'
 **/

// TODO: move down to TableFilterControls component once it gets converted
type FilterFormat =
  | 'dateRange'
  | 'currencyRange'
  | 'hoursRange'
  | 'weeksRange'
  | 'id';

type RenderFilteredTableProps<T extends object> = Omit<
  TableProps<T>,
  'rowClassName'
> & {
  filters: Record<string, unknown[] | unknown>;
  onClearAllFilters: () => void;
  onClearFilter: (column: string, value?: string | null) => void;
  columns: { filterFormat?: FilterFormat }[];
  rowClassName?: (record: T) => string | undefined;
  headerRightSlot?: React.ReactNode;
  filtersDataReady?: boolean;
};

// AntD declares this as sending sorter as possibly a list, so
// you will need to guard against a list arg by using instanceof
// Note: list will happen if you send dataIndex prop as list
export type RenderTableOnChange<T> = TableProps<T>['onChange'];

// copy of antd one + custom props
export type ColumnFilterItem = {
  text: React.ReactNode;
  labelText?: React.ReactNode;
  value: string | number | boolean;
  children?: ColumnFilterItem[];
};

// Helper type to define non-inline columns.
// Note: if you get cryptic errors/issues defining your columns
// its likely that you've missed or wrongly declared something
export type RenderTableColumn<
  T extends object,
  K extends keyof T = keyof T,
> = Omit<TableColumnType<T>, 'render' | 'dataIndex' | 'filters'> &
  NonNullable<
    | {
        [prop in K]: {
          filterFormat?: FilterFormat;
          filters?: ColumnFilterItem[];
          dataIndex: prop;
          render?: (value: T[prop], record: T) => React.ReactNode;
        };
      }[K]
    | {
        filterFormat?: FilterFormat;
        filters?: ColumnFilterItem[];
        dataIndex?: undefined | (number | string)[];
        render?: (value: unknown, record: T) => React.ReactNode;
      }
  >;

export const RenderFilteredTable = <T extends object>({
  columns,
  scroll,
  filters,
  onClearAllFilters,
  onClearFilter,
  rowClassName,
  onRow,
  headerRightSlot,
  filtersDataReady = true,
  ...props
}: RenderFilteredTableProps<T>) => (
  // do not put any state or logic here, this component is render only.
  <section className="FilteredTable">
    <Row justify="space-between" style={{ width: '100%', flexWrap: 'nowrap' }}>
      <Col flex="1 1 auto">
        {filtersDataReady && (
          <TableFilterControls
            filters={filters}
            clearAllFilters={onClearAllFilters}
            clearFilter={onClearFilter}
            columns={columns}
          />
        )}
      </Col>
      <Col style={{ minWidth: 'fit-content' }}>
        {headerRightSlot && (
          <div className="header-right-slot">{headerRightSlot}</div>
        )}
      </Col>
    </Row>
    <Table
      {...props}
      onRow={onRow}
      columns={columns}
      scroll={scroll ?? stableScroll}
      rowClassName={(record) => {
        const isRowClickable = !!onRow?.(record)?.onClick;

        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore: preserving js version behavior
        const recordClassName: string = record.rowClassName;
        const effectiveClassName = recordClassName || rowClassName?.(record);

        return clsx(
          {
            clickable: isRowClickable,
            'data-row': true,
          },
          effectiveClassName
        );
      }}
    />
    <style jsx>
      {`
        .FilteredTable {
          margin-top: 12px;
        }
        .FilteredTable :global(.ant-table-tbody > tr.disabled-row:hover > td) {
          background: ${Colors.steelLightest};
        }
        .FilteredTable :global(.disabled-row td) {
          background: ${Colors.steelLightest};
        }
        // FIXME: this global affects everywhere outside of this component
        //  and shouldn't be here.
        :global(.ant-table-filter-dropdown .ant-dropdown-menu-item-selected) {
          background-color: white;
        }
        // FIXME: this global affects everywhere outside of this component
        //  and shouldn't be here.
        :global(.ant-dropdown-menu-item) {
          display: flex;
        }
        .FilteredTable :global(tr.highlight-action-required > td:first-child) {
          border-left: solid 4px ${Colors.elm};
        }
        .FilteredTable :global(tr.highlight-action-required) {
          font-weight: bold;
        }
        .FilteredTable :global(.clickable) {
          cursor: pointer;
        }
        .FilteredTable
          :global(
            tr.highlight-recently-updated .ant-table-cell-fix-left,
            tr.highlight-recently-updated .ant-table-cell-fix-right,
            tr.highlight-recently-updated
          ) {
          background: ${Colors.elmHyperlight};
        }

        .FilteredTable :global(.ant-table-cell-fix-right) {
          z-index: 1;
        }

        .FilteredTable .header-right-slot {
          margin-bottom: 12px;
        }
      `}
    </style>
  </section>
);

RenderFilteredTable.defaultProps = {
  rowKey: 'key',
};
